import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { OsasEvaluationData } from './types';

export type OsasEvaluationReadKey =
    | keyof OsasEvaluationData
    | 'mraAttachment'
    | 'sptReport'
    | 'lifestyleAttachment'
    | 'papReport';

export type OsasEvaluationReadLabel =
    | '95% druk'
    | 'Anamnese'
    | 'Behandelend arts'
    | 'Beleid'
    | 'Bijlage'
    | 'Centraal apneu'
    | 'Cheyne-stokes'
    | 'Contact met patiënt gehad?'
    | 'Druk EPAP (Exhale)'
    | 'Druk IPAP (Inhale)'
    | 'Druk'
    | 'Extra waarden uit rapport'
    | 'Gemiddeld gebruik'
    | 'Huisarts op de hoogte brengen'
    | 'Klachtenlast'
    | 'Maximale druk'
    | 'Minimale druk'
    | 'Nachten met ondersteuning'
    | 'Obstructief apneu'
    | 'pAHI'
    | 'Periode report'
    | 'Proefperiode'
    | 'Rapport'
    | 'Rugligging'
    | 'SPO2'
    | 'Therapie gestart op'
    | 'Toelichting'
    | 'Type'
    | 'Vervolgevaluatie bij OSAS consulent'
    | 'Volgt patiënt het beleid?';

export type OsasEvaluationReadSectionTitle =
    | 'Anamnese'
    | 'Apparaat en instellingen'
    | 'Beleid'
    | 'Gebruik'
    | 'Informeren'
    | 'Leefstijlverandering'
    | 'MRA'
    | 'PAP rapport'
    | 'SPT rapport'
    | 'Waarden';

export const osasEvaluationReadSchema: TypedReadSchema<
    OsasEvaluationReadKey,
    OsasEvaluationReadLabel,
    OsasEvaluationReadSectionTitle
>[] = [
    {
        items: [
            { key: 'treatingPhysicianPractitionerId', label: 'Behandelend arts' },
            { key: 'assets', label: 'Bijlage' },
        ],
    },
    {
        items: [
            { key: 'patientFollowsMraPolicy', label: 'Volgt patiënt het beleid?' },
            { key: 'mraAttachment', label: 'Bijlage', type: 'assets' },
        ],
        sectionTitle: 'MRA',
    },
    {
        items: [
            { key: 'papReportStartDate', label: 'Periode report' },
            {
                key: 'papReport',
                label: 'Rapport',
                type: 'assets',
            },
        ],
        sectionTitle: 'PAP rapport',
    },
    {
        items: [
            { key: 'type', label: 'Type' },
            { key: 'pressureCpap', label: 'Druk' },
            {
                key: 'pressureApapMinimum',
                label: 'Minimale druk',
            },
            {
                key: 'pressureApapMaximum',
                label: 'Maximale druk',
            },
            {
                key: 'pressureApapNinetyfive',
                label: '95% druk',
            },
            {
                key: 'pressureIpap',
                label: 'Druk IPAP (Inhale)',
            },
            {
                key: 'pressureEpap',
                label: 'Druk EPAP (Exhale)',
            },
        ],
        sectionTitle: 'Apparaat en instellingen',
    },
    {
        items: [
            { key: 'papNightsWithSupport', label: 'Nachten met ondersteuning' },
            {
                append: 'uur / nacht',
                key: 'papAverageUse',
                label: 'Gemiddeld gebruik',
            },
        ],
        sectionTitle: 'Gebruik',
    },
    {
        items: [
            {
                append: '/ uur',
                key: 'pAhi3Percent',
                label: 'pAHI',
            },
            {
                append: '/ uur',
                key: 'obstructiveApnea',
                label: 'Obstructief apneu',
            },
            {
                append: '/ uur',
                key: 'centralApnea',
                label: 'Centraal apneu',
            },
            {
                key: 'cheyneStokes',
                label: 'Cheyne-stokes',
            },
            {
                key: 'oxygenSaturation',
                label: 'SPO2',
            },
            {
                key: 'extraValuesReport',
                label: 'Extra waarden uit rapport',
            },
        ],
        sectionTitle: 'Waarden',
    },
    {
        items: [
            { key: 'sptReportStartDate', label: 'Periode report' },
            {
                key: 'sptReport',
                label: 'Rapport',
                type: 'assets',
            },
        ],
        sectionTitle: 'SPT rapport',
    },
    {
        items: [
            {
                key: 'sptNightsWithSupport',
                label: 'Nachten met ondersteuning',
            },
            {
                append: 'uur / nacht',
                key: 'sptAverageUse',
                label: 'Gemiddeld gebruik',
            },
        ],
        sectionTitle: 'Gebruik',
    },
    {
        items: [
            {
                key: 'supinePositionFrom',
                label: 'Rugligging',
            },
            {
                key: 'sptRemark',
                label: 'Toelichting',
            },
        ],
        sectionTitle: 'Waarden',
    },
    {
        items: [
            { key: 'patientFollowsLifestylePolicy', label: 'Volgt patiënt het beleid?' },
            { key: 'lifestyleAttachment', label: 'Bijlage', type: 'assets' },
        ],
        sectionTitle: 'Leefstijlverandering',
    },
    {
        items: [
            { key: 'hadContactWithPatient', label: 'Contact met patiënt gehad?', type: 'boolean' },
            { key: 'medicalHistory', label: 'Anamnese' },
            { key: 'complaintBurden', label: 'Klachtenlast' },
            { key: 'trialPeriod', label: 'Proefperiode' },
            { key: 'therapyStartDate', label: 'Therapie gestart op', type: 'dateAsString' },
            { key: 'remark', label: 'Toelichting' },
        ],
        sectionTitle: 'Anamnese',
    },
    {
        items: [
            { key: 'policy', label: 'Beleid' },
            {
                append: 'weken',
                key: 'followUpEvaluationWeeks',
                label: 'Vervolgevaluatie bij OSAS consulent',
                type: 'number',
            },
        ],
        sectionTitle: 'Beleid',
    },
    {
        items: [{ key: 'shouldNotifyGeneralPractitioner', label: 'Huisarts op de hoogte brengen', type: 'boolean' }],
        sectionTitle: 'Informeren',
    },
];
